import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { getPendingCount, syncAllPending } from "./offlineQueue";

/**
 * Tracks browser connectivity, offline-queue size, and auto-syncs on reconnect.
 *
 * @returns {{ isOnline: boolean, pendingCount: number, syncing: boolean, refreshPending: () => void }}
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const wasOffline = useRef(!navigator.onLine);

  const refreshPending = useCallback(() => {
    getPendingCount()
      .then(setPendingCount)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Auto-sync when transitioning from offline → online, or on mount with stale items
  useEffect(() => {
    if (!isOnline) {
      wasOffline.current = true;
      refreshPending();
      return;
    }

    // Either we just came back online, or we loaded fresh with pending items
    wasOffline.current = false;

    // Check if there's anything to sync
    getPendingCount().then((count) => {
      setPendingCount(count);
      if (count === 0) return;

      setSyncing(true);
      syncAllPending()
        .then(({ synced, failed }) => {
          if (synced > 0) {
            toast.success(
              `${synced} offline submission${synced > 1 ? "s" : ""} synced`,
            );
          }
          if (failed > 0) {
            toast.error(
              `${failed} submission${failed > 1 ? "s" : ""} failed to sync`,
            );
          }
        })
        .catch(() => {
          toast.error("Failed to sync offline submissions");
        })
        .finally(() => {
          setSyncing(false);
          refreshPending();
        });
    });
  }, [isOnline, refreshPending]);

  return { isOnline, pendingCount, syncing, refreshPending };
}
